import { motion } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { ENGINE_STAGES, ENGINE_LINKS } from "../lib/about";
import { BlueprintGrid } from "../components/BlueprintGrid";
import { fadeUp, viewportOnce, stagger } from "../lib/motion";

const EngineDiagram = () => (
    <section
        data-testid="engine-section"
        className="relative overflow-hidden border-y border-white/10 bg-ink py-24 lg:py-36"
    >
        <BlueprintGrid />
        <div className="relative mx-auto grid max-w-[1600px] gap-16 px-6 md:px-10 lg:grid-cols-12 lg:px-16">
            <motion.div
                variants={fadeUp}
                initial="hidden"
                whileInView="show"
                viewport={viewportOnce}
                className="lg:col-span-5"
            >
                <div className="lg:sticky lg:top-28">
                    <div className="flex items-center gap-6">
                        <p className="font-mono text-[13px] tracking-[0.3em] text-mist">
                            THE BUILDEASE INTELLIGENCE ENGINE
                        </p>
                    </div>
                    <h2
                        data-testid="engine-headline"
                        className="mt-10 font-display text-4xl font-semibold leading-[1.1] text-white md:text-5xl"
                    >
                        Intelligence behind every construction decision
                        <span className="text-accent">.</span>
                    </h2>
                    <p className="mt-8 max-w-md text-[16px] leading-relaxed text-mist">
                        At the centre of BuildEase is a connected intelligence
                        engine designed to bring together architectural
                        information, construction knowledge, cost intelligence
                        and decision-support capabilities.
                    </p>
                    <p className="mt-5 max-w-md text-[16px] leading-relaxed text-mist">
                        Rather than treating every construction decision as an
                        isolated task, BuildEase connects information across
                        the residential building journey — architectural
                        information can inform quantities, quantities can
                        inform cost estimates, and cost information can help
                        homeowners better understand quotations and budget
                        implications.
                    </p>
                    <p className="mt-5 max-w-md text-[16px] leading-relaxed text-mist">
                        By connecting these capabilities, BuildEase aims to
                        give homeowners a more complete understanding of their
                        construction projects.
                    </p>
                </div>
            </motion.div>

            <motion.div
                variants={stagger}
                initial="hidden"
                whileInView="show"
                viewport={viewportOnce}
                data-testid="engine-flow"
                className="lg:col-span-7"
            >
                {ENGINE_STAGES.map((stage, i) => (
                    <motion.div
                        key={stage.n}
                        variants={fadeUp}
                        data-testid={`engine-stage-${stage.n.toLowerCase().replace(".", "-")}`}
                        className="relative"
                    >
                        <div
                            className={`relative border bg-surface/80 px-7 py-7 md:px-9 ${
                                stage.engine
                                    ? "border-accent/70 shadow-[0_0_60px_rgba(255,91,0,0.08)]"
                                    : "border-white/12"
                            }`}
                        >
                            <div className="flex items-center justify-between gap-4">
                                <p
                                    className={`font-mono text-[13px] tracking-[0.3em] ${
                                        stage.engine
                                            ? "text-accent"
                                            : "text-cad"
                                    }`}
                                >
                                    {stage.n}
                                </p>
                                {stage.engine ? (
                                    <p className="bg-accent px-2.5 py-1 font-mono text-[13px] tracking-[0.22em] text-white">
                                        THE ENGINE
                                    </p>
                                ) : null}
                            </div>
                            <h3
                                className={`mt-4 font-display text-xl font-semibold tracking-wide md:text-2xl ${
                                    stage.engine ? "text-accent" : "text-white"
                                }`}
                            >
                                {stage.title}
                            </h3>
                            <p className="mt-3 max-w-md text-[15px] leading-relaxed text-mist">
                                {stage.copy}
                            </p>
                        </div>

                        {i < ENGINE_STAGES.length - 1 ? (
                            <div
                                aria-hidden="true"
                                className="flex flex-col items-center gap-1.5 py-5"
                            >
                                <span className="h-4 w-px bg-white/25" />
                                <p className="flex items-center gap-2.5 font-mono text-[13px] tracking-[0.22em] text-cad">
                                    <ArrowDown className="h-3.5 w-3.5 text-accent" />
                                    {ENGINE_LINKS[i]}
                                </p>
                                <span className="h-4 w-px bg-white/25" />
                            </div>
                        ) : null}
                    </motion.div>
                ))}
            </motion.div>
        </div>
    </section>
);

export default EngineDiagram;
